"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { get, put, post, upload } from "../../utils/service";
import { formatMoney, formatLabel, parseApiError, today } from "@/utils/finance";
import { btnPrimary, btnGhost, inputCls, labelCls } from "@/components/finance/Modal";

interface Meta { base_currency: string; categories: string[]; currencies: string[]; receipt_max_kb: number }
interface Preview { exchange_rate: string; base_amount: string; base_currency: string }

export default function ExpenseFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [meta, setMeta] = useState<Meta | null>(null);
  const [form, setForm] = useState({ title: "", category: "supplies", category_other: "", type: "reimbursement", expense_date: today(), original_amount: "", original_currency: "UGX", notes: "" });
  const isPlanned = form.type === "planned_purchase";
  const [receipt, setReceipt] = useState<File | null>(null);
  const [existingReceipt, setExistingReceipt] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    get<{ data: Meta }>("/expenses/meta").then((r) => setMeta(r.data));
    if (editing) {
      get<{ data: { expense: Record<string, string | boolean | null> } }>(`/expenses/${id}`).then((r) => {
        const e = r.data.expense;
        setForm({ title: String(e.title), category: String(e.category), category_other: String(e.category_other ?? ""), type: String(e.type ?? "reimbursement"), expense_date: String(e.expense_date).slice(0, 10), original_amount: String(e.original_amount), original_currency: String(e.original_currency), notes: String(e.notes ?? "") });
        setExistingReceipt(e.has_receipt ? String(e.receipt_name ?? "Receipt") : null);
      }).catch(() => navigate("/expenses"));
    }
  }, [editing, id, navigate]);

  // Live estimate from the exact same converter the server uses on save.
  useEffect(() => {
    const amount = parseFloat(form.original_amount);
    if (!meta || !(amount > 0) || form.original_currency.length !== 3) { setPreview(null); setPreviewError(""); return; }
    const t = setTimeout(async () => {
      try {
        const r = await get<{ data: Preview }>("/expenses/preview-rate", { amount: form.original_amount, currency: form.original_currency });
        setPreview(r.data);
        setPreviewError("");
      } catch (e) {
        setPreview(null);
        setPreviewError(parseApiError(e).message);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.original_amount, form.original_currency, meta]);

  const save = async (submit: boolean, e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    setFieldErrors({});
    try {
      let expenseId = id;
      if (editing) {
        await put(`/expenses/${id}`, form);
        if (receipt) { const fd = new FormData(); fd.append("receipt", receipt); await upload(`/expenses/${id}/receipt`, fd); }
        if (submit) await post(`/expenses/${id}/submit`);
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
        fd.append("submit", submit ? "1" : "0");
        if (receipt) fd.append("receipt", receipt);
        const r = await upload<{ data: { expense: { id: string } } }>("/expenses", fd);
        expenseId = r.data.expense.id;
      }
      navigate(`/expenses/${expenseId}`);
    } catch (err) {
      const f = parseApiError(err);
      setError(f.message);
      setFieldErrors(f.fieldErrors);
    } finally {
      setBusy(false);
    }
  };

  const err = (k: string) => fieldErrors[k] && <p className="mt-1 text-xs text-red-600">{fieldErrors[k]}</p>;
  const foreign = meta && form.original_currency !== meta.base_currency;

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title={editing ? "Edit expense" : "New expense"} description="Record money you paid on the company's behalf">
          <Link to={editing ? `/expenses/${id}` : "/expenses"} className={btnGhost}>Cancel</Link>
        </PageHeader>

        <form onSubmit={(e) => save(true, e)} className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
          {!editing && (
            <div>
              <label className={labelCls}>What kind of expense is this?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => set("type", "reimbursement")} className={`text-left rounded-lg border px-3 py-2 text-xs transition ${!isPlanned ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-gray-200 hover:bg-gray-50"}`}>
                  <span className="block font-semibold text-gray-900">Already spent</span>
                  <span className="block text-gray-500 mt-0.5">You paid for something and want to be reimbursed.</span>
                </button>
                <button type="button" onClick={() => set("type", "planned_purchase")} className={`text-left rounded-lg border px-3 py-2 text-xs transition ${isPlanned ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-gray-200 hover:bg-gray-50"}`}>
                  <span className="block font-semibold text-gray-900">Request approval to buy something</span>
                  <span className="block text-gray-500 mt-0.5">Nothing has been spent yet — ask for approval first.</span>
                </button>
              </div>
            </div>
          )}
          <div><label className={labelCls}>What {isPlanned ? "do you want to buy" : "was it for"}?</label><input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={255} />{err("title")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Category</label><select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>{meta?.categories.map((c) => <option key={c} value={c}>{formatLabel(c)}</option>)}</select>{err("category")}</div>
            {form.category === "other" && (
              <div className="sm:col-span-2"><label className={labelCls}>Please specify the category</label><input className={inputCls} value={form.category_other} onChange={(e) => set("category_other", e.target.value)} placeholder="e.g. Courier fees, Permit renewal…" maxLength={100} required autoFocus />{err("category_other")}</div>
            )}
            <div>
              <label className={labelCls}>{isPlanned ? "Date needed" : "Date of expense"}</label>
              <DatePicker value={form.expense_date} onChange={(v) => set("expense_date", v)} min={isPlanned ? today() : undefined} max={isPlanned ? undefined : today()} required />
              {err("expense_date")}
            </div>
            <div><label className={labelCls}>{isPlanned ? "Estimated amount" : "Amount you paid"}</label><input className={inputCls} type="number" step="0.01" min="0.01" value={form.original_amount} onChange={(e) => set("original_amount", e.target.value)} required />{err("original_amount")}</div>
            <div>
              <label className={labelCls}>Currency</label>
              <select className={inputCls} value={form.original_currency} onChange={(e) => set("original_currency", e.target.value)}>{(meta?.currencies ?? [form.original_currency]).map((c) => <option key={c} value={c}>{c}</option>)}</select>
              {err("original_currency")}
            </div>
          </div>

          {meta && (
            <div className="rounded-lg bg-primary/10 px-3 py-2 text-xs text-gray-700">
              {previewError ? <span className="text-red-600">{previewError}</span> : preview && foreign ? (
                <>{isPlanned ? "Estimated at about" : "You'll be reimbursed about"} <strong>{formatMoney(preview.base_amount, preview.base_currency)}</strong> <span className="text-gray-500">(1 {form.original_currency} = {parseFloat(preview.exchange_rate).toLocaleString(undefined, { maximumFractionDigits: 4 })} {preview.base_currency}). The rate is locked when you save; your original amount is always kept.</span></>
              ) : <>{isPlanned ? "Estimate is calculated" : "Reimbursement is calculated"} in <strong>{meta.base_currency}</strong>. {foreign ? "Enter an amount to see the conversion." : "No conversion needed."}</>}
            </div>
          )}

          <div><label className={labelCls}>Notes (optional)</label><textarea className={inputCls} rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />{err("notes")}</div>
          {!isPlanned && (
            <div>
              <label className={labelCls}>Receipt (optional)</label>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="block w-full text-xs" onChange={(e) => setReceipt(e.target.files?.[0] ?? null)} />
              <p className="mt-1 text-[11px] text-gray-500">PDF or image, up to {meta ? Math.round(meta.receipt_max_kb / 1024) : 5} MB. {existingReceipt && !receipt && `Current file: ${existingReceipt} (choose a file to replace it).`}</p>
              {err("receipt")}
            </div>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} disabled={busy} onClick={() => save(false)}>Save as draft</button>
            <button className={btnPrimary} disabled={busy}>{busy ? "Saving…" : "Submit for approval"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
